import React from 'react';
import { makeObservable, observable, action, computed } from 'mobx';
import { observer } from 'mobx-react';

import { defaulComparator, stringSearchService } from '@services/StringSearchService';

import { ModalButtonType, type IModalDialogContent, type ModalDialogOptions, type ModalWindow } from '@components/Modal';
import type { ChatResponseItem } from '@root/models';

type SettingsDialogProps = {
  onClose: () => void;
  chats: ChatResponseItem[];
  onSelectedChat: (chatId: string) => void;
};

@observer
export default class SettingsDialog extends React.Component<SettingsDialogProps> implements IModalDialogContent<void> {
  @observable
  private _searchTerm: string = "";

  @observable
  private _chats: ChatResponseItem[] = [];

  private _window: ModalWindow<void> | null = null;

  constructor(props: SettingsDialogProps) {
    super(props);
    this._chats = props.chats;
    makeObservable(this);
  }

  componentDidUpdate(prevProps: SettingsDialogProps) {
    if (prevProps.chats !== this.props.chats) {
      this._chats = this.props.chats;
    }
  }

  public getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
    this._window = window;
    return {
      title: 'Search Chats',
      closeByEscape: true
    };
  }

  @action
  private _onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this._searchTerm = event.target.value;
  };

  @action
  private clearSearch = () => {
    this._searchTerm = '';
  };

  @computed
  get searchTerms(){
    const searchStr = this._searchTerm;
    return searchStr.split(/[ ,]+/).filter(x => !!x);
  }
  
  @computed
  private get filteredChats(): ChatResponseItem[] {
    let filteredList = this._chats;
    
    // Apply search filter
    const searchStringParts = this.searchTerms;
    if (searchStringParts.length) {
      filteredList = filteredList.filter((chat): boolean => {
        let result = true;

        for(const term of searchStringParts){
          let anyMatch = false;

          if (defaulComparator(chat.name, term, 0) !== -1){
              anyMatch = true;
          }

          if (chat.keywords && chat.keywords.length > 0 && defaulComparator(chat.keywords.join(' '), term, 0) !== -1){
            anyMatch = true;
          }

          if (!anyMatch){
              result = false;
          }
        }

        return result;
      });
    }
    
    return filteredList;
  }

  @action
  private _onChatSelect = (chatId: string) => {
    this.props.onSelectedChat(chatId);
    this._window?.close(ModalButtonType.Close); // Close the modal after selection
  };

  private _renderText = (text: string | undefined | null) => {
    const { searchTerms } = this;

    const parts = stringSearchService.markMatches(text, searchTerms, defaulComparator);
    return (<>
      {parts.map((p, index) => p.isMatch ? <span className="text-match" key={'t' + index}>{p.text}</span> : p.text)}
    </>);
  };

  render() {
    return (
      <>
        <style>{`
          .search-chat-search-container {
            display: flex;
            flex-direction: column;
            gap: 16px;
            max-width: 600px;
            height: 500px;
            max-height: 60vh;
          }

          .search-input-container {
            display: flex;
            align-items: center;
            gap: 8px;
            position: relative;
          }

          .search-input {
            flex: 1;
            padding: 10px 12px;
            background-color: var(--input-bg);
            color: var(--text-primary);
            border: 1px solid var(--border-color);
            border-radius: 6px;
            font-size: 14px;
            outline: none;
            transition: border-color 0.2s ease;
          }

          .search-input:focus {
            border-color: var(--accent-color);
          }

          .search-clear-btn {
            position: absolute;
            right: 8px;
            background: none;
            border: none;
            color: var(--text-muted);
            cursor: pointer;
            font-size: 16px;
            padding: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: color 0.2s ease;
          }

          .search-clear-btn:hover {
            color: var(--text-primary);
          }

          .results-count {
            font-size: 12px;
            color: var(--text-muted);
            padding: 0 4px;
          }

          .search-chat-list {
            flex: 1;
            overflow-y: auto;
            border: 1px solid var(--border-color);
            border-radius: 6px;
            background-color: var(--bg-primary);
          }

          .empty-state {
            padding: 20px;
            text-align: center;
            color: var(--text-muted);
            font-size: 14px;
          }

          .search-chat-item {
            padding: 12px 16px;
            border-bottom: 1px solid var(--border-color);
            cursor: pointer;
            transition: background-color 0.2s ease;
            background-color: transparent;
          }

          .search-chat-item:hover {
            background-color: var(--hover-bg);
          }

          .search-chat-item:last-child {
            border-bottom: none;
          }

          .search-chat-name {
            font-size: 14px;
            font-weight: 500;
            color: var(--text-primary);
            margin-bottom: 4px;
          }

          .search-chat-keywords {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
            margin-top: 6px;
          }

          .keyword-tag {
            font-size: 11px;
            background-color: var(--bg-secondary);
            color: var(--text-secondary);
            padding: 2px 6px;
            border-radius: 3px;
            border: 1px solid var(--border-color);
          }

          .search-chat-date {
            font-size: 11px;
            color: var(--text-muted);
            margin-top: 4px;
          }

          @media (max-width: 768px) {
            .search-chat-search-container {  
              max-height: 75vh;
            }
            .modal-content{  
              max-height: 95vh;
            }
          }
        `}</style>

        <div className="search-chat-search-container">
          {/* Search Input */}
          <div className="search-input-container">
            <input
              type="text"
              placeholder="Search chats by name or keywords..."
              value={this._searchTerm}
              onChange={this._onSearchChange}
              className="search-input"
              autoFocus
            />
            {this._searchTerm && (
              <button
                onClick={this.clearSearch}
                className="search-clear-btn"
              >
                ×
              </button>
            )}
          </div>

          {/* Results Count */}
          <div className="results-count">
            {this.filteredChats.length} of {this._chats.length} chats
          </div>

          {/* Chat List */}
          <div className="search-chat-list">
            {this.filteredChats.length === 0 ? (
              <div className="empty-state">
                {this._searchTerm ? 'No chats found matching your search' : 'No chats available'}
              </div>
            ) : (
              this.filteredChats.map(chat => (
                <div
                  key={chat.id}
                  onClick={() => this._onChatSelect(chat.id)}
                  className="search-chat-item"
                >
                  <div className="search-chat-name">
                    {this._renderText(chat.name) || 'Untitled Chat'}
                  </div>
                  
                  {chat.keywords && chat.keywords.length > 0 && (
                    <div className="search-chat-keywords">
                      {chat.keywords.filter(x => !!x).map((keyword, index) => (
                        <span key={index} className="keyword-tag">
                          {this._renderText(keyword)}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {chat.modifiedAt && (
                    <div className="search-chat-date">
                      Last modified: {new Date(chat.modifiedAt).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </>
    );
  }
}